import { readyAudioStatuses } from '@listening/api-contract/audio-statuses';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import {
  getLibrarySortMode,
  type LibrarySortMode,
  librarySortParams,
  useLibrarySortMode,
} from '../../stores/library-sort-store';
import {
  AUDIO_DETAIL_STALE_TIME_MS,
  type AudioItem,
  AudioItems,
  audioItemSummaryQueryKey,
} from '../audio-items';
import { processingQueuePollingDelayMs } from '../processing-queue-polling';
import { useEq } from '../runtime-context';

export { readyAudioStatusSet } from '@listening/api-contract/audio-statuses';

// The FIXED, NAMED set of audio-list reader hooks. This is the bounded
// replacement for the single drained list cache: every audio view reads through
// one of these named hooks, and the query keys are constructed ONLY here. There
// is intentionally **no** generic `useAudioList(params)` — callers cannot pass
// arbitrary sort/status/cursor/folder options, so the set of audio caches stays
// finite and knowable. That invariant is what lets the optimistic walk
// (`walk-audio-caches.ts`) be correct: it can never miss a cache it doesn't know
// about. See docs/projects/complete/web/query-stack-route-loaders.md ("Scaling problem
// + decision"). This whole folder is deleted when TanStack DB SSR (db#709) lands.
//
// The library and folder lists carry ONE parameter beyond their identity: the
// user's persisted `LibrarySortMode`, which the server has to order by (a
// client-side title sort can only order the pages fetched so far). It is a
// closed two-value set, so the caches stay finite, and the mode is part of
// every key — two orderings can never overwrite each other under one key. Both
// arg builders default it from storage so route loaders and the non-React
// cache readers don't have to thread it.

const AUDIO_ITEMS_STALE_TIME_MS = 24 * 60 * 60 * 1000; // 24h
// Background poll so the always-mounted surfaces (library / processing queue /
// continue-listening) stay live without a manual refresh: an in-progress
// conversion flips to `complete` server-side, or another client uploads — the
// web app surfaces both within one interval. Pauses while the tab is
// backgrounded.
//
// COST: refetching an infinite query re-fetches ALL currently-loaded pages (no
// `maxPages` cap), so a deep-scrolled library re-fetches every loaded page each
// interval — it scales with scroll depth, not "one page". This is the same
// behavior the pre-migration `useAudioListAndWatchDeltas` had (an infinite query
// with `refetchInterval: 20s`), so it's not a regression, and it's still
// strictly cheaper than that era's other variant which re-drained the *entire*
// library. The proper fix is delta-based polling (a cheap `if_modified_since_ms`
// "did anything change?" check that only invalidates on a hit) — tracked in
// docs/projects/backlog/backend/list-and-get-audio-endpoints-v3.md (#8).
const AUDIO_ITEMS_POLL_INTERVAL_MS = 20 * 1000; // 20s
let usesExternalAudioListPolling = false;
const audioItemsRefetchInterval = () =>
  usesExternalAudioListPolling ? false : AUDIO_ITEMS_POLL_INTERVAL_MS;

export function setExternalAudioListPollingEnabled(enabled: boolean) {
  usesExternalAudioListPolling = enabled;
}
// Page size for the infinite library/folder lists. Smaller than a full drain;
// the sentinel loads more on scroll.
const PAGE_SIZE = 50;
// Free-plan upload counting only cares about *this week's* uploads; a free user
// with more than this in one week is already well over the limit, so one bounded
// page is sufficient (no drain).
const UPLOADS_THIS_WEEK_PAGE_SIZE = 50;

// —— Query keys (constructed ONLY in this module) ——

export const libraryListQueryKey = (
  sortMode: LibrarySortMode = getLibrarySortMode(),
) => ['audioItems', 'list', 'library', sortMode] as const;
export const audioItemsListRootQueryKey = ['audioItems', 'list'] as const;
export const processingQueueQueryKey = [
  'audioItems',
  'list',
  'processing',
] as const;
export const continueListeningQueryKey = [
  'audioItems',
  'list',
  'continue',
] as const;
export const uploadsThisWeekQueryKey = [
  'audioItems',
  'list',
  'uploadsThisWeek',
] as const;
export const folderPickerQueryKey = [
  'audioItems',
  'list',
  'folderPicker',
] as const;
// The picker's search needle is a KEY SUFFIX, not a client-side filter: the
// server does the matching, so a needle nobody matches costs one request
// instead of walking the whole library page by page. It stays a suffix so
// `reconcileFolderAssignment`'s `partialMatchKey` exclusion still spares every
// needle's cache.
export const folderPickerSearchQueryKey = (titleQuery: string) =>
  [...folderPickerQueryKey, titleQuery] as const;
export const folderAudiosQueryKey = (
  folderId: string,
  sortMode: LibrarySortMode = getLibrarySortMode(),
) => ['audioItems', 'list', 'folder', folderId, sortMode] as const;
export const unfiledAudiosQueryKey = (
  sortMode: LibrarySortMode = getLibrarySortMode(),
) => ['audioItems', 'list', 'unfiled', sortMode] as const;

// —— Infinite list args (eq-agnostic; route loaders wrap with
//    `getEq().infiniteQueryOptions(...)` + `ensureInfiniteQueryData`) ——

type PageResult = {
  items: ReadonlyArray<AudioItem>;
  nextCursor?: string | undefined;
  ttsFinalizedVersion?: number;
  voiceDataVersion?: number;
};

type AudioListData = { pages: ReadonlyArray<PageResult> };
type AudioListRefetchQuery = {
  state: { data: AudioListData | undefined };
};
type AudioListRefetchInterval =
  | number
  | false
  | ((query: AudioListRefetchQuery) => number | false);

const makeInfiniteArgs = (
  queryKey: readonly unknown[],
  params: {
    sort_by: 'created_at' | 'played_at' | 'title';
    sort_dir?: 'asc' | 'desc';
  } & (
    | { status: ReadonlyArray<AudioItem['conversion_status']> }
    | { except_status: ReadonlyArray<AudioItem['conversion_status']> }
  ) & { folder_id?: string; title_query?: string },
  opts?: { refetchInterval?: AudioListRefetchInterval; staleTime?: number },
) => ({
  queryKey,
  queryFn: (ctx: { pageParam: string | undefined }) =>
    AudioItems.listPageEffect({
      ...params,
      limit: PAGE_SIZE,
      cursor: ctx.pageParam,
    }),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: PageResult) => last.nextCursor,
  staleTime: opts?.staleTime ?? AUDIO_ITEMS_STALE_TIME_MS,
  ...(opts?.refetchInterval === undefined
    ? {}
    : { refetchInterval: opts.refetchInterval }),
});

export const libraryListInfiniteArgs = (
  sortMode: LibrarySortMode = getLibrarySortMode(),
) =>
  makeInfiniteArgs(
    libraryListQueryKey(sortMode),
    { ...librarySortParams(sortMode), status: readyAudioStatuses },
    { refetchInterval: audioItemsRefetchInterval },
  );

export const processingQueueInfiniteArgs = makeInfiniteArgs(
  processingQueueQueryKey,
  { sort_by: 'created_at', except_status: readyAudioStatuses },
  {
    refetchInterval: (query) =>
      usesExternalAudioListPolling
        ? processingQueuePollingDelayMs(query.state.data)
        : AUDIO_ITEMS_POLL_INTERVAL_MS,
  },
);

export const folderAudiosInfiniteArgs = (
  folderId: string,
  sortMode: LibrarySortMode = getLibrarySortMode(),
) =>
  makeInfiniteArgs(folderAudiosQueryKey(folderId, sortMode), {
    ...librarySortParams(sortMode),
    status: readyAudioStatuses,
    folder_id: folderId,
  });

export const unfiledAudiosInfiniteArgs = (
  sortMode: LibrarySortMode = getLibrarySortMode(),
) =>
  makeInfiniteArgs(
    unfiledAudiosQueryKey(sortMode),
    {
      ...librarySortParams(sortMode),
      status: readyAudioStatuses,
      folder_id: 'root',
    },
    { refetchInterval: audioItemsRefetchInterval },
  );

export const folderPickerInfiniteArgs = (titleQuery: string) =>
  makeInfiniteArgs(
    folderPickerSearchQueryKey(titleQuery),
    { sort_by: 'title', status: readyAudioStatuses, title_query: titleQuery },
    { staleTime: 0 },
  );

// —— Single-page args ——

export const continueListeningArgs = {
  queryKey: continueListeningQueryKey,
  queryFn: () =>
    AudioItems.listPageEffect({
      sort_by: 'played_at',
      status: readyAudioStatuses,
      limit: 10,
    }),
  staleTime: AUDIO_ITEMS_STALE_TIME_MS,
  refetchInterval: audioItemsRefetchInterval,
} as const;

export const libraryListBoundedArgs = (
  limit: number,
  sortMode: LibrarySortMode = getLibrarySortMode(),
) => ({
  queryKey: ['audioItems', 'list', 'library-bounded', limit, sortMode] as const,
  queryFn: () =>
    AudioItems.listPageEffect({
      ...librarySortParams(sortMode),
      status: readyAudioStatuses,
      limit,
    }),
  staleTime: AUDIO_ITEMS_STALE_TIME_MS,
  refetchInterval: audioItemsRefetchInterval,
});

export const uploadsThisWeekArgs = {
  queryKey: uploadsThisWeekQueryKey,
  queryFn: () =>
    AudioItems.listPageEffect({
      sort_by: 'created_at',
      except_status: 'Error',
      limit: UPLOADS_THIS_WEEK_PAGE_SIZE,
    }),
  staleTime: AUDIO_ITEMS_STALE_TIME_MS,
} as const;

// —— Helpers ——

// Flatten an infinite-list cache into a single ordered array (server order
// preserved). Consumers apply presentational filter/sort/search on top.
export const flattenAudioPages = (
  data: { pages: ReadonlyArray<PageResult> } | undefined,
): ReadonlyArray<AudioItem> => (data ? data.pages.flatMap((p) => p.items) : []);

// —— Read hooks ——

// Suspense readers for loader-backed route bodies (resolve warm; the route
// loader prefetched the cache). Return the full infinite query so the caller can
// drive the scroll sentinel via `hasNextPage` / `fetchNextPage`.
export const useLibraryList = () =>
  useSuspenseInfiniteQuery(
    useEq().infiniteQueryOptions(libraryListInfiniteArgs(useLibrarySortMode())),
  );

export const useProcessingQueue = () =>
  useSuspenseInfiniteQuery(
    useEq().infiniteQueryOptions(processingQueueInfiniteArgs),
  );

// `keepPreviousData` holds the last needle's rows on screen while the next
// needle's page loads, so typing doesn't blink the list through a spinner.
export const useFolderPickerList = (titleQuery: string) =>
  useInfiniteQuery({
    ...useEq().infiniteQueryOptions(folderPickerInfiniteArgs(titleQuery)),
    placeholderData: keepPreviousData,
  });

export const useFolderAudios = (folderId: string) =>
  useSuspenseInfiniteQuery(
    useEq().infiniteQueryOptions(
      folderAudiosInfiniteArgs(folderId, useLibrarySortMode()),
    ),
  );

export const useUnfiledAudios = (opts?: { enabled?: boolean }) =>
  useInfiniteQuery({
    ...useEq().infiniteQueryOptions(
      unfiledAudiosInfiniteArgs(useLibrarySortMode()),
    ),
    enabled: opts?.enabled ?? true,
  });

// Non-suspense library reader for ambient UI (nav "recent", cmd-k) that may
// render on routes whose loader didn't prefetch the library — must not suspend.
export const useLibraryListQuery = () =>
  useInfiniteQuery(
    useEq().infiniteQueryOptions(libraryListInfiniteArgs(useLibrarySortMode())),
  );

// Continue-listening: server returns the 10 most-recently-played ready audios
// (sort=played_at); `selectContinueListening` drops never-started padding and
// keeps recency order. Non-suspense + auth-gated (PlayBar mounts on the public
// `/doc/$docId` SSR route where the list endpoint 401s).
export const useContinueListening = (opts?: { enabled?: boolean }) =>
  useQuery({
    ...useEq().queryOptions(continueListeningArgs),
    enabled: opts?.enabled ?? true,
    select: (page: PageResult) =>
      AudioItems.selectContinueListening(page.items),
  });

// Free-plan upload counter: one bounded page of non-errored audios (server
// sort=created_at). The consumer filters by week start client-side.
export const useUploadsThisWeek = (opts?: { enabled?: boolean }) =>
  useQuery({
    ...useEq().queryOptions(uploadsThisWeekArgs),
    enabled: opts?.enabled ?? true,
    select: (page: PageResult) => page.items,
  });

// Single audio by id (GetAudio summary). Replaces the old "scan the full list
// with rows.find(id)" pattern for note/share lookups.
export const useAudioById = (audioId: string | undefined) =>
  useQuery({
    ...useEq().queryOptions({
      queryKey: audioItemSummaryQueryKey(audioId ?? ''),
      queryFn: () => AudioItems.detailEffect(audioId ?? ''),
      staleTime: AUDIO_DETAIL_STALE_TIME_MS,
    }),
    enabled: !!audioId,
  });
